import { create, windowedFiniteBatchScheduler } from "@yornaath/batshit";
import { z } from "zod";

import fetch from "@lib/fetch";

import type { RemoteLookupProvider } from "./providers/types";
import type { FindResult } from "./types";

/**
 * Creates a fetch batcher for fetching items by id for a given type and app
 * group. The batcher will batch requests that occur within a 50ms window, and
 * will fetch up to 1,000 items in a single request.
 *
 * See yornaath/batshit for details: https://github.com/yornaath/batshit
 */
export function createFindBatcher<T extends string = string, Id = unknown, Item = unknown>(
  appGroupId: string,
  type: T,
  provider: RemoteLookupProvider<Id, Item, unknown>
) {
  const fetchResultSchema = z.object({
    items: z.array(provider.validators.item),
  });
  return create<Record<string, Item>, Id, FindResult<Item>>({
    name: `remote-lookup-${appGroupId}-${type}-find`,
    fetcher: async (queries: Id[], signal) => {
      const rawResult: unknown = await fetch(
        `/remote_lookup/${appGroupId}/${type}/find`,
        "POST",
        {
          ids: queries,
        },
        { signal }
      );
      const parsedResult = fetchResultSchema.parse(rawResult);

      const itemsByIdString: Record<string, Item> = {};
      for (const item of parsedResult.items) {
        const id = provider.mapItemToId(item);
        const idString = provider.mapIdToString(id);
        itemsByIdString[idString] = item;
      }
      return itemsByIdString;
    },
    resolver: (itemsByIdString, query) => {
      const idString = provider.mapIdToString(query);
      const item = itemsByIdString[idString] || null;
      return { item };
    },
    scheduler: windowedFiniteBatchScheduler({
      windowMs: 50,
      maxBatchSize: 1_000,
    }),
  });
}
